// Chakra Imports
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
  Link,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { useLocation } from 'react-router-dom';
import AdminNavbarLinks from "components/navbar/NavbarLinksAdmin";
import routes from 'routes.js';

export default function AdminNavbar(props) {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.addEventListener("scroll", changeNavbar);

    return () => {
      window.removeEventListener("scroll", changeNavbar);
    };
  });

  const { secondary, message, brandText } = props;

  // Get current route information based on location
  const getCurrentRoute = () => {
    const currentPath = location.pathname;
    
    // Find matching route
    for (let i = 0; i < routes.length; i++) {
      const route = routes[i];
      if (route.collapse) {
        const collapseRoute = getCurrentRouteFromItems(route.items, currentPath);
        if (collapseRoute) return collapseRoute;
      } else if (route.category) {
        const categoryRoute = getCurrentRouteFromItems(route.items, currentPath);
        if (categoryRoute) return categoryRoute;
      } else {
        const fullPath = route.layout + route.path;
        if (currentPath === fullPath || 
          (route.layout === '/admin' && currentPath.startsWith(fullPath))) {
          return route;
        }
      }
    }
    return null;
  };

  const getCurrentRouteFromItems = (items, currentPath) => {
    for (let i = 0; i < items.length; i++) {
      const route = items[i];
      if (route.collapse) {
        const collapseRoute = getCurrentRouteFromItems(route.items, currentPath);
        if (collapseRoute) return collapseRoute;
      } else if (route.category) {
        const categoryRoute = getCurrentRouteFromItems(route.items, currentPath);
        if (categoryRoute) return categoryRoute;
      } else {
        const fullPath = route.layout + route.path;
        if (currentPath === fullPath || 
          (route.layout === '/admin' && currentPath.startsWith(fullPath))) {
          return route;
        }
      }
    }
    return null;
  };

  // Generate breadcrumb based on current path
  const getBreadcrumbs = () => {
    const breadcrumbs = [];
    const pathSegments = location.pathname.split('/').filter(segment => segment !== '');
    
    // Only show breadcrumbs if we have more than 2 path segments (e.g., /admin/agents/something)
    // This means we skip single-level routes like /admin/dashboard, /admin/agents, etc.
    if (pathSegments.length > 2) {
      // Add "Pages" as the first breadcrumb for multi-level paths
      breadcrumbs.push({ name: 'Pages', href: '#' });
      
      // Generate breadcrumbs from path segments, excluding the layout part
      for (let i = 1; i < pathSegments.length; i++) {
        const segment = pathSegments[i];
        const formattedName = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
        const partialPath = '/' + pathSegments.slice(0, i + 1).join('/');
        
        breadcrumbs.push({ 
          name: formattedName, 
          href: partialPath 
        });
      }
    }
    
    return breadcrumbs;
  };

  // Get current page title based on location
  const getCurrentPageTitle = () => {
    const currentRoute = getCurrentRoute();
    
    if (currentRoute && currentRoute.name) {
      return currentRoute.name;
    }
    
    // Fallback to path-based title
    const pathSegments = location.pathname.split('/').filter(segment => segment !== '');
    if (pathSegments.length >= 2) {
      const currentPage = pathSegments[pathSegments.length - 1];
      return currentPage.charAt(0).toUpperCase() + currentPage.slice(1).replace(/-/g, ' ');
    }
    
    return 'Dashboard';
  };

  // Here are all the props that may change depending on navbar's type or state.(secondary, variant, scrolled)
  let mainText = useColorModeValue("navy.700", "white");
  let secondaryText = useColorModeValue("gray.700", "white");
  let navbarPosition = "fixed";
  let navbarFilter = "none";
  let navbarBackdrop = "blur(20px)";
  let navbarShadow = "none";
  let navbarBg = useColorModeValue(
    "rgba(244, 247, 254, 0.2)",
    "rgba(11,20,55,0.5)"
  );
  let navbarBorder = "transparent";
  let secondaryMargin = "0px";
  let paddingX = "15px";
  let gap = "0px";
  const changeNavbar = () => {
    if (window.scrollY > 1) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  };

  return (
    <Box
      position={navbarPosition}
      boxShadow={navbarShadow}
      bg={navbarBg}
      borderColor={navbarBorder}
      filter={navbarFilter}
      backdropFilter={navbarBackdrop}
      backgroundPosition='center'
      backgroundSize='cover'
      borderRadius='16px'
      borderWidth='1.5px'
      borderStyle='solid'
      transitionDelay='0s, 0s, 0s, 0s'
      transitionDuration=' 0.25s, 0.25s, 0.25s, 0s'
      transition-property='box-shadow, background-color, filter, border'
      transitionTimingFunction='linear, linear, linear, linear'
      alignItems={{ xl: "center" }}
      display={secondary ? "block" : "flex"}
      minH='75px'
      justifyContent={{ xl: "center" }}
      lineHeight='25.6px'
      mx='auto'
      mt={secondaryMargin}
      pb='8px'
      left={{ base: "12px", md: "30px", lg: "30px", xl: "30px" }}
      px={{
        sm: paddingX,
        md: "10px",
      }}
      ps={{
        xl: "12px",
      }}
      pt='8px'
      top={{ base: "12px", md: "16px", xl: "18px" }}
      w={{
        base: "calc(100vw - 6%)",
        md: "calc(100vw - 8%)",
        lg: "calc(100vw - 6%)",
        xl: "calc(100vw - 350px)",
        "2xl": "calc(100vw - 365px)",
      }}>
      <Flex
        w='100%'
        flexDirection={{
          sm: "column",
          md: "row",
        }}
        alignItems={{ xl: "center" }}
        mb={gap}>
        <Box mb={{ sm: "8px", md: "0px" }}>
          {getBreadcrumbs().length > 0 && (
            <Breadcrumb>
              {getBreadcrumbs().map((breadcrumb, index) => (
                <BreadcrumbItem key={index} color={secondaryText} fontSize='sm' mb='5px'>
                  <BreadcrumbLink href={breadcrumb.href} color={secondaryText}>
                    {breadcrumb.name}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              ))}
            </Breadcrumb>
          )}
          {/* Here we create navbar brand, based on route name */}
          <Link
            color={mainText}
            href='#'
            bg='inherit'
            borderRadius='inherit'
            fontWeight='bold'
            fontSize='34px'
            _hover={{ color: { mainText } }}
            _active={{
              bg: "inherit",
              transform: "none",
              borderColor: "transparent",
            }}
            _focus={{
              boxShadow: "none",
            }}>
            {getCurrentPageTitle()}
          </Link>
        </Box>
        <Box ms='auto' w={{ sm: "100%", md: "unset" }}>
          <AdminNavbarLinks
            onOpen={props.onOpen}
            logoText={props.logoText}
            secondary={props.secondary}
            fixed={props.fixed}
            scrolled={scrolled}
          />
        </Box>
      </Flex>
      {secondary ? <Text color='white'>{message}</Text> : null}
    </Box>
  );
}

AdminNavbar.propTypes = {
  brandText: PropTypes.string,
  variant: PropTypes.string,
  secondary: PropTypes.bool,
  fixed: PropTypes.bool,
  onOpen: PropTypes.func,
};
